/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, Input, OnChanges, SimpleChanges, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {interpolateWarning} from './interpolate-warnings-filter';

import * as _ from 'lodash';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';
import {deferSignalStateWrite} from '../../util/template-state';

@Component({
  imports: [CommonModule],
  selector: 'validation-warnings',
  template: `
    <div *ngIf="usesFormState">
      <div *ngIf="showValidationError || showBulkError" class="alert alert-danger">
        <p *ngIf="showValidationError">Your submission had a validation error. Adjust your input according to the details below.</p>
        <p *ngIf="showBulkError">Your bulk edits had a validation error when applied to one of your selections. Adjust your edit according to the details below or change your selections.</p>
        <ul *ngIf="hasResolvedErrors">
          <li *ngFor="let error of resolvedApiErrors; trackBy: trackByApiError">{{interpolateWarning(error.apiText)}}</li>
        </ul>
      </div>
      <!-- Must be on one line with no white space to ensure :empty CSS style works -->
      <div class="alert alert-danger" *ngIf="displayPreventsAdServing.length > 0 && extra"><p>This {{ extra }} cannot deliver until the warnings are resolved.</p></div>
      <div class="alert alert-warning" *ngIf="displayPreventsAdServing.length > 0"><p *ngFor="let warning of displayPreventsAdServing; trackBy: trackByWarning">{{ interpolateWarning(warning) }}</p></div>
      <div class="alert alert-info" *ngIf="displayStandardWarnings.length > 0"><p *ngFor="let warning of displayStandardWarnings; trackBy: trackByWarning">{{ interpolateWarning(warning) }}</p></div>
    </div>
  `
})
export class ValidationWarningsComponent implements OnChanges, DoCheck {
  @Input() warnings: any;
  @Input() hasValidationError: boolean;
  @Input() apiErrors: Array<{ key: string; apiText: string }>;
  @Input() clientErrors: any[];
  @Input() bulkError: boolean;
  /** Supports simple form-state objects used by bulk-action modals. */
  @Input() form: any;
  @Input() skips: [string];
  @Input() extra: string;

  private readonly displayPreventsAdServingState = signal<any[]>([]);
  private readonly displayStandardWarningsState = signal<any[]>([]);

  get displayPreventsAdServing(): any[] { return this.displayPreventsAdServingState(); }
  set displayPreventsAdServing(value: any[]) { this.displayPreventsAdServingState.set(value); }
  get displayStandardWarnings(): any[] { return this.displayStandardWarningsState(); }
  set displayStandardWarnings(value: any[]) { this.displayStandardWarningsState.set(value); }

  private warningListsInitialized = false;
  private readonly usesFormStateState = signal(false);
  private readonly validationErrorState = signal(false);
  private readonly bulkErrorState = signal(false);
  private readonly resolvedErrorsState = signal(false);

  constructor(@Inject(LocalNetworkProfile) private LocalNetworkProfile) {
  }

  get usesFormState(): boolean {
    return this.usesFormStateState();
  }

  get showValidationError(): boolean {
    return this.validationErrorState();
  }

  get showBulkError(): boolean {
    return this.bulkErrorState();
  }

  get resolvedApiErrors(): Array<{ key: string; apiText: string }> {
    if (this.apiErrors !== undefined) {
      return this.apiErrors;
    }
    return this.form?.apiErrors || [];
  }

  get resolvedClientErrors(): any[] {
    if (this.clientErrors !== undefined) {
      return this.clientErrors;
    }
    return this.form?.clientErrors || [];
  }

  /** Use > 0 — `length || length` binds the numeric length (1→2 NG0100). */
  get hasResolvedErrors(): boolean {
    return this.resolvedErrorsState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.syncFlags();
    if (changes.warnings || changes.skips) {
      this.syncWarningLists();
    }
  }

  ngDoCheck(): void {
    this.syncFlags();
  }

  trackByApiError(index: number, error: {key?: string; apiText?: string}): string {
    return error?.key || error?.apiText || String(index);
  }

  trackByWarning(index: number, warning: {code?: string; message?: string}): string {
    return warning?.code || warning?.message || String(index);
  }

  interpolateWarning(warning) {
    return interpolateWarning(warning, this.LocalNetworkProfile.isPureMarketplacePlatform());
  }

  private syncWarningLists(): void {
    const nextPrevents = this.computePreventsAdServing();
    const nextStandard = this.computeStandardWarnings();
    if (!this.warningListsInitialized) {
      this.warningListsInitialized = true;
      this.displayPreventsAdServing = nextPrevents;
      this.displayStandardWarnings = nextStandard;
      return;
    }
    // Parent often swaps validationWarnings during the same CD turn as form settle.
    deferSignalStateWrite(() => {
      this.displayPreventsAdServing = nextPrevents;
      this.displayStandardWarnings = nextStandard;
    });
  }

  private syncFlags(): void {
    this.usesFormStateState.set(this.hasValidationError !== undefined
      || this.apiErrors !== undefined
      || this.clientErrors !== undefined
      || this.bulkError !== undefined
      || !!this.form);
    this.validationErrorState.set(this.hasValidationError !== undefined
      ? !!this.hasValidationError
      : !!this.form?.validationError);
    this.bulkErrorState.set(this.bulkError !== undefined ? !!this.bulkError : !!this.form?.bulkError);
    this.resolvedErrorsState.set(this.resolvedApiErrors.length > 0 || this.resolvedClientErrors.length > 0);
  }

  private computePreventsAdServing(): any[] {
    const theSkips = this.skips;
    return _.filter(this.warnings, function(w) {
      return w.preventsAdServing && w.code !== 'validation.warning.user.segment.global.missing.team' && !_.includes(theSkips, w.code);
    });
  }

  private computeStandardWarnings(): any[] {
    const theSkips = this.skips;
    return _.filter(this.warnings, function(w) {
      return !w.preventsAdServing && w.code !== 'validation.warning.user.segment.global.missing.team' && !_.includes(theSkips, w.code);
    });
  }
}
